import { CRS, latLng, type LatLngTuple, type Map as LeafletMap, type Point } from 'leaflet';
import { isEndWaypoint, isStartWaypoint, type RouteWaypoint } from './dayRoutes';

const MERGE_RADIUS_PX = 28;
const SEPARATION_ZOOM_MARGIN = 0.25;
const MAX_JOINED_LABELS = 3;
const GROUP_KEY_SEPARATOR = '|';
const STOP_LABEL_SEPARATOR = ',';

export interface ColoredWaypoint extends RouteWaypoint {
  dayColor: string;
  date: string;
}

export interface WaypointGroup {
  key: string;
  position: LatLngTuple;
  members: ColoredWaypoint[];
  isCluster: boolean;
}

export interface MarkerContent {
  hasStart: boolean;
  isEndOnly: boolean;
  stopText: string;
}

interface ProjectedWaypoint {
  order: number;
  waypoint: ColoredWaypoint;
  pixel: Point;
}

export const getStopMembers = (members: ColoredWaypoint[]): ColoredWaypoint[] =>
  members.filter((member) => !isStartWaypoint(member));

export const hasStartMember = (members: ColoredWaypoint[]): boolean =>
  members.some(isStartWaypoint);

export function getPrimaryMember(members: ColoredWaypoint[]): ColoredWaypoint | undefined {
  const [firstStop] = getStopMembers(members);
  if (firstStop) return firstStop;
  return members.reduce<ColoredWaypoint | undefined>(
    (latestStart, member) =>
      latestStart && latestStart.date >= member.date ? latestStart : member,
    undefined,
  );
}

function getCentroid(members: ColoredWaypoint[]): LatLngTuple {
  const latitude = members.reduce((sum, member) => sum + member.position[0], 0) / members.length;
  const longitude = members.reduce((sum, member) => sum + member.position[1], 0) / members.length;
  return [latitude, longitude];
}

function buildWaypointGroup(members: ColoredWaypoint[]): WaypointGroup {
  const stopCount = getStopMembers(members).length;
  const dayCount = new Set(members.map((member) => member.date)).size;
  const isCluster = stopCount > 0 && (dayCount > 1 || stopCount > MAX_JOINED_LABELS);
  const [firstMember] = members;

  return {
    key: members.map((member) => member.key).join(GROUP_KEY_SEPARATOR),
    position: isCluster || !firstMember ? getCentroid(members) : firstMember.position,
    members,
    isCluster,
  };
}

function groupNearbyWaypoints(
  waypoints: ColoredWaypoint[],
  map: LeafletMap,
  zoom: number,
): WaypointGroup[] {
  const pixelClusters: ProjectedWaypoint[][] = [];

  waypoints.forEach((waypoint, order) => {
    const projected = { order, waypoint, pixel: map.project(waypoint.position, zoom) };
    const touchingClusters = pixelClusters.filter((cluster) =>
      cluster.some((member) => member.pixel.distanceTo(projected.pixel) <= MERGE_RADIUS_PX),
    );
    for (const cluster of touchingClusters) pixelClusters.splice(pixelClusters.indexOf(cluster), 1);
    pixelClusters.push([...touchingClusters.flat(), projected]);
  });

  return pixelClusters.map((cluster) =>
    buildWaypointGroup(
      cluster
        .toSorted((left, right) => left.order - right.order)
        .map((projected) => projected.waypoint),
    ),
  );
}

const projectAtZoom = (position: LatLngTuple, zoom: number): Point =>
  CRS.EPSG3857.latLngToPoint(latLng(position), zoom);

export function getZoomToSeparateFromNeighbors(
  position: LatLngTuple,
  neighborPositions: LatLngTuple[],
  zoom: number,
): number | null {
  const positionPixel = projectAtZoom(position, zoom);
  const mergingDistancesPx = neighborPositions
    .map((neighborPosition) => projectAtZoom(neighborPosition, zoom).distanceTo(positionPixel))
    .filter((distancePx) => distancePx <= MERGE_RADIUS_PX);
  if (mergingDistancesPx.length === 0) return null;

  const separableDistancesPx = mergingDistancesPx.filter((distancePx) => distancePx > 0);
  if (separableDistancesPx.length === 0) return Number.POSITIVE_INFINITY;
  const nearestDistancePx = Math.min(...separableDistancesPx);
  return zoom + Math.log2(MERGE_RADIUS_PX / nearestDistancePx) + SEPARATION_ZOOM_MARGIN;
}

function separateFromCluster(groups: WaypointGroup[], waypointKey: string): WaypointGroup[] {
  return groups.flatMap((group) => {
    const separatedMember = group.members.find((member) => member.key === waypointKey);
    if (!group.isCluster || !separatedMember) return [group];

    const remainingMembers = group.members.filter((member) => member !== separatedMember);
    return [buildWaypointGroup(remainingMembers), buildWaypointGroup([separatedMember])];
  });
}

export function groupWaypoints(
  waypoints: ColoredWaypoint[],
  map: LeafletMap,
  zoom: number,
  focusedKey: string | null,
): WaypointGroup[] {
  const starts = waypoints.filter(isStartWaypoint);
  const stops = getStopMembers(waypoints);
  const groups = [
    ...groupNearbyWaypoints(stops, map, zoom),
    ...groupNearbyWaypoints(starts, map, zoom),
  ];
  return focusedKey ? separateFromCluster(groups, focusedKey) : groups;
}

export function getMarkerContent(members: ColoredWaypoint[]): MarkerContent {
  const stopMembers = getStopMembers(members);
  const [onlyStop, ...otherStops] = stopMembers;
  return {
    hasStart: hasStartMember(members),
    isEndOnly: onlyStop !== undefined && otherStops.length === 0 && isEndWaypoint(onlyStop),
    stopText: stopMembers.map((member) => member.label).join(STOP_LABEL_SEPARATOR),
  };
}
